'use client'

/**
 * batch-71 kiosk-magazijn (FO-532 §C9) - de ENE camera-schil voor het scannen van een
 * QR-code. Vervangt vier kopieën (portal scanner en doorlopende scanner, backoffice
 * /m/voorraad scan en telling). Maak er geen vijfde van.
 *
 * - `BarcodeDetector` als de browser het kan, anders de zxing-terugval.
 * - `@zxing/browser` wordt door de app meegegeven (`laadZxing`, een dynamische import):
 *   dit package heeft die dependency niet, zodat een app die niet scant hem nooit bundelt.
 * - Een start die nog op de camera wacht als er gestopt wordt, ruimt zijn eigen stream op
 *   (elke start heeft een volgnummer), zodat er nooit een tweede camera openblijft.
 * - Geen geheugen voor dubbele scans: dat beslist de aanroeper (zie `QrScanner`).
 */

import { useCallback, useRef, useState, type RefObject } from 'react'

export type QrCameraStand = 'uit' | 'starten' | 'aan' | 'geweigerd' | 'fout'

interface BarcodeDetectorLike {
  detect(image: HTMLVideoElement): Promise<Array<{ rawValue: string }>>
}

interface BarcodeDetectorCtor {
  new (options?: { formats: string[] }): BarcodeDetectorLike
}

/** De twee dingen die we van `@zxing/browser` gebruiken. */
export interface ZxingModule {
  BrowserQRCodeReader: new () => {
    decodeFromVideoElement(
      video: HTMLVideoElement,
      callback: (result: { getText(): string } | undefined | null) => void,
    ): Promise<{ stop: () => void }>
  }
}

export type LaadZxing = () => Promise<ZxingModule>

const DETECTIE_INTERVAL_MS = 180

export function useQrCamera({
  videoRef,
  onTekst,
  laadZxing,
}: {
  videoRef: RefObject<HTMLVideoElement>
  /** Elke herkende tekst, ongefilterd. De aanroeper haalt het token eruit en beslist. */
  onTekst: (tekst: string) => void
  laadZxing: LaadZxing
}) {
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<number | null>(null)
  const zxingRef = useRef<{ stop: () => void } | null>(null)
  const actiefRef = useRef(false)
  const pogingRef = useRef(0)
  const onTekstRef = useRef(onTekst)
  onTekstRef.current = onTekst

  const [stand, setStand] = useState<QrCameraStand>('uit')
  const [foutTekst, setFoutTekst] = useState('')
  const [zaklamp, setZaklamp] = useState(false)
  const [zaklampKan, setZaklampKan] = useState(false)

  const stop = useCallback(() => {
    pogingRef.current += 1
    actiefRef.current = false
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (zxingRef.current) {
      try {
        zxingRef.current.stop()
      } catch {
        // al gestopt
      }
      zxingRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (videoRef.current) videoRef.current.srcObject = null
    setZaklamp(false)
    setZaklampKan(false)
  }, [videoRef])

  const start = useCallback(async () => {
    if (actiefRef.current) return
    const poging = ++pogingRef.current
    const verlopen = () => poging !== pogingRef.current
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setFoutTekst('Deze browser kan de camera niet gebruiken. Typ de code van het label.')
      setStand('fout')
      return
    }
    setStand('starten')
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
    } catch (err) {
      if (verlopen()) return
      const naam = (err as { name?: string }).name
      if (naam === 'NotAllowedError' || naam === 'PermissionDeniedError') setStand('geweigerd')
      else {
        setFoutTekst('Camera niet beschikbaar. Typ de code van het label.')
        setStand('fout')
      }
      return
    }
    if (verlopen()) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }
    streamRef.current = stream
    actiefRef.current = true
    const track = stream.getVideoTracks()[0]
    const capabilities = track?.getCapabilities?.() as Record<string, unknown> | undefined
    setZaklampKan(Boolean(capabilities?.torch))

    const video = videoRef.current
    if (!video) {
      stop()
      setStand('uit')
      return
    }
    video.srcObject = stream
    try {
      await video.play()
    } catch {
      // autoplay geblokkeerd: de stream loopt toch
    }
    if (verlopen()) return
    setStand('aan')

    const BD = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
    if (BD) {
      let detector: BarcodeDetectorLike | null = null
      try {
        detector = new BD({ formats: ['qr_code'] })
      } catch {
        detector = null
      }
      if (detector) {
        const d = detector
        const ronde = async () => {
          if (!actiefRef.current) return
          const v = videoRef.current
          if (v && v.readyState >= 2) {
            try {
              const codes = await d.detect(v)
              for (const c of codes) onTekstRef.current(c.rawValue)
            } catch {
              // los frame mislukt: gewoon door
            }
          }
          if (actiefRef.current) timerRef.current = window.setTimeout(ronde, DETECTIE_INTERVAL_MS)
        }
        timerRef.current = window.setTimeout(ronde, DETECTIE_INTERVAL_MS)
        return
      }
    }

    try {
      const { BrowserQRCodeReader } = await laadZxing()
      const reader = new BrowserQRCodeReader()
      const controls = await reader.decodeFromVideoElement(video, (result) => {
        // Geen QR in beeld is normaal: dan is er geen result.
        if (result) onTekstRef.current(result.getText())
      })
      if (verlopen() || !actiefRef.current) controls.stop()
      else zxingRef.current = controls
    } catch {
      if (verlopen()) return
      stop()
      setFoutTekst('De camera-scanner kon niet starten. Typ de code van het label.')
      setStand('fout')
    }
  }, [laadZxing, stop, videoRef])

  const schakelZaklamp = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (!track) return
    try {
      await track.applyConstraints({ advanced: [{ torch: !zaklamp }] } as unknown as MediaTrackConstraints)
      setZaklamp(!zaklamp)
    } catch {
      setZaklampKan(false)
    }
  }, [zaklamp])

  return {
    stand,
    setStand,
    foutTekst,
    start,
    stop,
    isActief: () => actiefRef.current,
    zaklamp,
    zaklampKan,
    schakelZaklamp,
  }
}
